import ko from "knockout";
import loggingService from "services/loggingService";

/**
 * Custom binding handler for gux dropdown options to ensure JAWS screenreader compatibility.
 *
 * For dropdowns with preloaded data, standard Knockout bindings work correctly with screenreaders.
 * However, for dynamically loaded dropdown content, JAWS screenreader fails to read option text content.
 * This binding handler explicitly creates gux-option/gux-option-multi elements with proper text content
 * to ensure accessibility for dynamically populated dropdowns.
 *
 * # Required Arguments
 *
 * @param {Array|Observable} options - Array of objects to be rendered as dropdown options
 *
 * # Optional Arguments
 *
 * @param {String} optionsText - Property name on each option object to use for display text. Defaults to 'text'.
 * @param {String} optionsValue - Property name on each option object to use for the value attribute. Defaults to 'value'.
 * @param {Object|Observable} selectedValue - Currently selected option object (single dropdowns only).
 *                                            Will be preserved with filtered="true" attribute during filtering.
 *
 * # Examples
 *
 *   <gux-listbox data-bind="guxDropdownOptions: {
 *       options: myOptionsArray,
 *       optionsText: 'name',
 *       optionsValue: 'id'
 *   }"></gux-listbox>
 *
 *   <gux-listbox data-bind="guxDropdownOptions: {
 *       options: myOptionsArray,
 *       optionsText: 'displayName',
 *       optionsValue: 'userId',
 *       selectedValue: currentSelection
 *   }"></gux-listbox>
 *
 *   <gux-listbox-multi data-bind="guxDropdownOptions: {
 *       options: myOptionsArray
 *   }"></gux-listbox-multi>
 *
 * @exports bindingHandlers/guxDropdownOptions
 */

const DEFAULT_TEXT_LABEL = "text";
const DEFAULT_VALUE_LABEL = "value";

const getItemProperties = (item, textProp, valueProp) => {
    let text = "";
    let value = "";

    if (textProp) {
        text = ko.unwrap(item[textProp]);
    } else if (item[DEFAULT_TEXT_LABEL]) {
        text = ko.unwrap(item[DEFAULT_TEXT_LABEL]);
    } else {
        loggingService.warn("guxDropdownOptions: No text property found");
    }

    if (valueProp) {
        value = ko.unwrap(item[valueProp]);
    } else if (item[DEFAULT_VALUE_LABEL]) {
        value = ko.unwrap(item[DEFAULT_VALUE_LABEL]);
    } else {
        loggingService.warn("guxDropdownOptions: No value property found");
    }

    return { text: text || "", value: value || "" };
};

ko.bindingHandlers.guxDropdownOptions = {
    init: function () {
        return { controlsDescendantBindings: true };
    },
    update: function (element, valueAccessor) {
        const { options, optionsText, optionsValue, selectedValue } =
            valueAccessor();
        const optionsList = ko.unwrap(options);
        const isMulti = element.tagName.toLowerCase().includes("multi");
        const optionTag = isMulti ? "gux-option-multi" : "gux-option";
        const fragment = document.createDocumentFragment();

        if (optionsList && optionsList.length > 0) {
            optionsList.forEach((item) => {
                const { text, value } = getItemProperties(
                    item,
                    optionsText,
                    optionsValue
                );

                if (!text && !value) {
                    return;
                }

                const optionElement = document.createElement(optionTag);
                optionElement.setAttribute("value", value);
                optionElement.textContent = text;
                fragment.appendChild(optionElement);
            });
        }

        // Preserve selected option with filtered="true" attribute (single dropdowns only)
        // This keeps the selected value displayed in the dropdown after filtering, even though it's hidden from the listbox
        const selected = ko.unwrap(selectedValue);
        if (selected && !isMulti) {
            const { text, value } = getItemProperties(
                selected,
                optionsText,
                optionsValue
            );

            if (text && value) {
                const selectedElement = document.createElement(optionTag);
                selectedElement.setAttribute("value", value);
                selectedElement.setAttribute("filtered", "true");
                selectedElement.textContent = text;
                fragment.appendChild(selectedElement);
            }
        }

        // Clear existing content and append new options in a single operation
        element.textContent = "";
        element.appendChild(fragment);
    },
};
